import { createFileRoute } from "@tanstack/react-router";
import { WandSparklesIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { SignInButton } from "@/web/auth/SignInButton";
import { Redirect } from "@/web/components/Redirect";
import { Spinner } from "@/web/components/primitives/spinner";
import { DefaultNavbar } from "@/web/components/styled/AppNavbar";
import {
  addPendingDocs,
  type ChatMessage,
  ChatPane,
  ConfirmationCard,
  fileToBase64,
  isWireMessage,
  type PendingDoc,
  useLeaseSignupCta,
} from "@/web/components/styled/LeaseChat";
import { ErrorHandler } from "@/web/lib/errors";
import { useFeatureFlag } from "@/web/lib/featureFlags";
import { parseGraderReply, serializeGraderReply } from "@/web/lib/leaseGraderConfirmation";
import {
  deriveLeaseHealth,
  formatMonths,
  type LeaseFinding,
  type LeaseHealth,
  type LeaseHealthLevel,
} from "@/web/lib/leaseHealth";
import { cn } from "@/web/lib/styling";
import { useProcedureMutation, useProcedureQueryLazy } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";

export const Route = createFileRoute("/lease-grader")({
  component: LeaseGraderRoute,
});

// ---------------------------------------------------------------------------
// Lease grader — an anonymous, top-of-funnel chatbot that parses a visitor's
// lease PDF(s) into a clean, color-coded LEASE ABSTRACT plus a lease-health
// assessment drawn only from the lease's own terms (docs/LEASE_GRADER.md).
//
// Scope note (2026-07): deliberately NO market analysis — no offer estimate, no
// rent-vs-market, no buy/rent/move/stay options. Our market data isn't good
// enough to support any of it yet. The sibling `/offer-recommendation` route
// (the retired offer-first paradigm) was deleted outright — not redirected —
// so this is the only lease route.
//
// No account required: identity is a client-minted session UUID in localStorage.
// Chat scaffolding lives in `LeaseChat.tsx`.
// ---------------------------------------------------------------------------

const LOCAL_STORAGE_KEY = "leaseGrader.sessionId";

const GREETING: ChatMessage = {
  role: "assistant",
  content:
    "Hi! Upload your commercial lease and I'll break down what you signed — the key terms, and which parts work for or against you. No account needed. If you have riders or amendments, add them all at once.",
};

function LeaseGraderRoute() {
  // Gate the whole feature behind the LEASE_GRADER flag. When off (including when
  // never set), render the 404 page — the app's convention for a hidden route.
  const enabled = useFeatureFlag("LEASE_GRADER");
  if (!enabled) return <Redirect to="/$" />;
  return <LeaseGraderInner />;
}

/** Read (or mint + persist) the anonymous session id from localStorage. */
function useSessionId(): string {
  const [sessionId] = useState(() => {
    if (typeof window === "undefined") return "";
    const existing = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    window.localStorage.setItem(LOCAL_STORAGE_KEY, minted);
    return minted;
  });
  return sessionId;
}

function LeaseGraderInner() {
  const sessionId = useSessionId();

  // Rehydrate the session's persisted transcript so a refresh doesn't lose the
  // conversation. Lazy (non-suspense) so we can gate the first render on it and
  // avoid a greeting-then-history flash. Gated on a real session id (SSR = "").
  const chatQuery = useProcedureQueryLazy(
    procedures.getLeaseGraderChat,
    { sessionId },
    { enabled: sessionId.length > 0 },
  );

  if (sessionId.length === 0 || chatQuery.isLoading) {
    return (
      <div className="flex h-dvh flex-col">
        <DefaultNavbar />
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  const persisted = chatQuery.data?.messages ?? [];
  return (
    <LeaseGraderChat sessionId={sessionId} initialMessages={messagesFromPersisted(persisted)} />
  );
}

/** Reconstruct chat bubbles from the persisted transcript (assistant turns come
 * back in their fenced form — split prose + confirmation via the shared codec). */
function messagesFromPersisted(rows: { role: string; content: string }[]): ChatMessage[] {
  if (rows.length === 0) return [GREETING];
  return rows.map((row) => {
    if (row.role !== "assistant") {
      return { role: "user", content: row.content };
    }
    const { message, confirmation } = parseGraderReply(row.content);
    return { role: "assistant", content: message, confirmation };
  });
}

function LeaseGraderChat({
  sessionId,
  initialMessages,
}: {
  sessionId: string;
  initialMessages: ChatMessage[];
}) {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState("");
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);

  const { mutateAsync: analyze, isPending } = useProcedureMutation(procedures.gradeLease);
  const { mutateAsync: createUploadUrl } = useProcedureMutation(
    procedures.createLeaseGraderUploadUrl,
  );
  const { mutateAsync: submitDoc } = useProcedureMutation(procedures.submitLeaseGraderDoc);
  const { maybeAppendSignupCta } = useLeaseSignupCta();

  // The most recent confirmation the assistant produced, and its assessment.
  // Derived ONCE here and passed down, so the abstract card's timing badge and
  // the health panel can never disagree.
  const latestConfirmation = useMemo(
    () =>
      [...messages].reverse().find((m) => m.role === "assistant" && m.confirmation)?.confirmation ??
      null,
    [messages],
  );
  const health = useMemo(
    () => (latestConfirmation ? deriveLeaseHealth(latestConfirmation) : null),
    [latestConfirmation],
  );

  function addDocs(files: FileList | File[]) {
    setPendingDocs((prev) => addPendingDocs(prev, files));
  }

  function removePendingDoc(id: string) {
    setPendingDocs((prev) => prev.filter((doc) => doc.id !== id));
  }

  async function handleSend() {
    const trimmed = input.trim();
    if ((!trimmed && pendingDocs.length === 0) || isPending) return;

    const docsThisTurn = pendingDocs;
    const attachments = docsThisTurn.map((doc) => doc.fileName);
    // The message the model sees: the user's text, plus a note of what they
    // attached so a docs-only turn still has content.
    const noteParts: string[] = [];
    if (trimmed) noteParts.push(trimmed);
    if (attachments.length > 0) {
      noteParts.push(
        `[Attached ${attachments.length} lease document(s): ${attachments.join(", ")}]`,
      );
    }
    const content = noteParts.join("\n\n");

    const userMessage: ChatMessage = {
      role: "user",
      content,
      attachments: attachments.length > 0 ? attachments : undefined,
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setPendingDocs([]);

    await requestReply(nextMessages, docsThisTurn);
  }

  /**
   * Read each attached PDF to base64 for the model, and (in parallel) upload it
   * to GCS for persistence. Then call `gradeLease` and append the reply.
   */
  async function requestReply(conversation: ChatMessage[], docsThisTurn: PendingDoc[]) {
    try {
      // Read to base64 (for the model) and persist to GCS (for durability) in
      // parallel. Persistence is best-effort — a hiccup shouldn't block analysis.
      const [documents] = await Promise.all([
        Promise.all(
          docsThisTurn.map(async (doc) => ({
            fileName: doc.fileName,
            base64: await fileToBase64(doc.file),
          })),
        ),
        Promise.all(
          docsThisTurn.map((doc) => persistDoc(doc).catch((e) => ErrorHandler.reportError(e))),
        ),
      ]);

      const wireMessages = conversation.filter(isWireMessage).map((m) => ({
        role: m.role,
        content:
          m.role === "assistant"
            ? serializeGraderReply(m.content, m.confirmation ?? null)
            : m.content,
      }));

      const reply = await analyze({ sessionId, messages: wireMessages, documents });
      const message = reply.message ?? "";
      const confirmation = reply.confirmation
        ? parseGraderReply(reply.confirmation).confirmation
        : null;
      setMessages((prev) => [...prev, { role: "assistant", content: message, confirmation }]);

      // Once we have a real assessment, drop a one-time in-chat sign-up nudge
      // below the reply (anonymous visitors only; at most once — see the hook).
      const assessed = deriveLeaseHealth(confirmation).hasEnoughData;
      maybeAppendSignupCta(
        assessed,
        "Want to keep this lease on file and get a heads-up before key dates? Create a free account — I can keep answering questions here either way.",
        setMessages,
      );
    } catch (error) {
      ErrorHandler.default(error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Something went wrong reading your lease. Please try again.",
          error: true,
        },
      ]);
    }
  }

  /** Upload one PDF to GCS via a signed URL, then record it for the session. */
  async function persistDoc(doc: PendingDoc): Promise<void> {
    const { uploadUrl, objectPath, contentType, contentLengthRange } = await createUploadUrl({
      sessionId,
      contentType: doc.file.type,
    });
    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "content-type": contentType, "x-goog-content-length-range": contentLengthRange },
      body: doc.file,
    });
    if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);
    await submitDoc({ sessionId, objectPath, originalFileName: doc.fileName });
  }

  async function handleRetry() {
    // Retry drops the trailing error bubble and re-sends with no new docs.
    const withoutError = messages.filter((m) => m.error !== true);
    setMessages(withoutError);
    await requestReply(withoutError, []);
  }

  return (
    <div className="flex h-dvh flex-col">
      <DefaultNavbar />
      {/* Desktop: two columns — chat on the left, the abstract + health panel
          stacked in a fixed-width right rail. Mobile: cards above the chat,
          collapsed to tap-to-expand headers. Until the first abstract exists
          there's no rail, so the chat stays a centered narrow column. */}
      <div
        className={cn(
          "mx-auto flex min-h-0 w-full flex-1 flex-col px-4 sm:flex-row-reverse sm:gap-4",
          latestConfirmation ? "max-w-6xl" : "max-w-2xl",
        )}
      >
        {latestConfirmation ? (
          <div className="flex min-h-0 flex-col gap-2 pt-4 sm:w-96 sm:shrink-0 sm:overflow-y-auto sm:pb-4">
            <ConfirmationCard confirmation={latestConfirmation} health={health} />
            <LeaseHealthPanel health={health} />
          </div>
        ) : null}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ChatPane
            messages={messages}
            input={input}
            pendingDocs={pendingDocs}
            isPending={isPending}
            pendingLabel="Reading your lease…"
            placeholder="Attach your lease PDF(s), or type a message…"
            onInputChange={setInput}
            onSend={handleSend}
            onRetry={handleRetry}
            onAddDocs={addDocs}
            onRemovePendingDoc={removePendingDoc}
          />
        </div>
      </div>
    </div>
  );
}

/** Headline copy per assessment level. Qualitative on purpose — no letter grade
 * or numeric score, which would imply a rubric we haven't validated. */
const HEALTH_LEVEL_COPY: Record<
  LeaseHealthLevel,
  { title: string; blurb: string; accent: string; badge: string }
> = {
  STRONG: {
    title: "Strong position",
    blurb: "Your lease gives you more leverage than most.",
    accent: "border-withus-green",
    badge: "bg-withus-green/10 text-withus-green",
  },
  BALANCED: {
    title: "Balanced",
    blurb: "A fairly standard lease — nothing alarming, nothing unusual in your favor.",
    accent: "border-withus-blue",
    badge: "bg-withus-blue/10 text-withus-blue",
  },
  NEEDS_ATTENTION: {
    title: "Needs attention",
    blurb: "There are terms or timing here worth acting on.",
    accent: "border-withus-yellow",
    badge: "bg-withus-yellow/20 text-withus-dark-blue",
  },
  UNKNOWN: {
    title: "Still reading",
    blurb: "Once we have your lease dates, we can assess where you stand.",
    accent: "border-muted",
    badge: "bg-muted text-muted-foreground",
  },
};

/**
 * The lease-health panel — the assessment beside the abstract. Runs the mirrored
 * engine over the latest abstract and renders a qualitative classification plus
 * color-coded advantages/disadvantages, all drawn from the lease's OWN terms.
 *
 * MARKET DATA ENTRYPOINT (future): a market read renders as its own sibling
 * panel below this one, visually distinct so an estimate never reads as a lease
 * fact. Don't fold market figures into these findings.
 */
function LeaseHealthPanel({ health }: { health: LeaseHealth | null }) {
  if (!health) return null;

  const level = HEALTH_LEVEL_COPY[health.level];
  const monthsLeft = health.timing.monthsToExpiry;

  return (
    <section className={cn("border border-l-2 bg-muted/40 p-4", level.accent)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <WandSparklesIcon aria-hidden className="size-4 shrink-0 text-withus-blue" />
          <h2 className="withus-strong text-sm">Lease health</h2>
        </div>
        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", level.badge)}>
          {level.title}
        </span>
      </div>

      <p className="mt-2 text-xs text-muted-foreground">
        {level.blurb}
        {health.timing.status === "active" && monthsLeft !== null
          ? ` ${formatMonths(monthsLeft)} left on the term.`
          : ""}
      </p>

      {health.advantages.length > 0 || health.disadvantages.length > 0 ? (
        <div className="mt-3 flex flex-col gap-1.5">
          {health.advantages.map((finding) => (
            <FindingRow key={finding.code} finding={finding} />
          ))}
          {health.disadvantages.map((finding) => (
            <FindingRow key={finding.code} finding={finding} />
          ))}
        </div>
      ) : null}

      <div className="mt-3 border-t pt-3">
        <p className="mb-2 text-xs text-muted-foreground">
          Save this lease to your account to track key dates and go deeper.
        </p>
        <SignInButton className="w-full" label="Create a free account" />
      </div>
    </section>
  );
}

/** One advantage/disadvantage, color-coded by tone. */
function FindingRow({ finding }: { finding: LeaseFinding }) {
  const isAdvantage = finding.tone === "ADVANTAGE";
  return (
    <div
      className={cn(
        "border-l-2 py-0.5 pl-2",
        isAdvantage ? "border-withus-green" : "border-withus-yellow",
      )}
    >
      <p
        className={cn(
          "text-xs font-medium",
          isAdvantage ? "text-withus-green" : "text-withus-dark-blue",
        )}
      >
        {isAdvantage ? "\u26a1" : "\u26a0\ufe0f"} {finding.label}
      </p>
      <p className="text-[11px] text-muted-foreground">{finding.detail}</p>
    </div>
  );
}
